import {
  CookieKey,
  ErrorMessages,
  MasterTranslation,
  MessageField,
  NewsletterOptInSource,
  SignUpSource,
} from '@/constants';
import { triggerHook } from '@/fetchers';
import { useGeo, useGlobalState } from '@/hooks';
import { FieldType, type IUserInfo, type LoginMethod } from '@/interfaces';
import type { LoginReplyFlags } from '@/interfaces/tracking/trackingOnboardingPopup.interfaces';
import { useEmailSyncContext, useMyParcelsContext } from '@/modules/pmt/context';
import { isJoinUsPopupCta, useLoginCta } from '@/modules/pmt/hooks/useLoginCta.hook';
import type { IPmtOnboardingPopup } from '@/modules/pmt/interfaces';
import { gtmClickJoinUs, gtmViewJoinUs } from '@/utils';
import { useTrackSignEvents } from '@/utils/GTM/useTrackSignEvents.hook';
import { useStatsigClient } from '@statsig/react-bindings';
import Cookies from 'js-cookie';
import getConfig from 'next/config';
import { useEffect, useMemo, useState } from 'react';

export const useSignIn = ({ identifier, subPmcNewsletterText, delayTime, zapierHookId }: IPmtOnboardingPopup) => {
  const { t, isFromGetNotifiedBtn, isFromEmailSyncBtn, isFollowingLinkedIn, isFromDelayedShipmentBtn } =
    useGlobalState();
  const { notificationTranslation } = useMyParcelsContext();
  const { isEmailSyncAllowed } = useEmailSyncContext();

  const { geoInfo } = useGeo();
  const [newsletterOptIn, setNewsletterOptIn] = useState<boolean>(false);
  const { logEvent } = useStatsigClient();
  const track = useTrackSignEvents();
  const { publicRuntimeConfig } = getConfig();

  if (!publicRuntimeConfig?.googleEmailSyncClientId) {
    console.error(ErrorMessages[MessageField.EMAIL_SYNC_CLIENT_ENV_NOT_FOUND].error);
  }

  const cta = useLoginCta();

  const ga4CommonParams = useMemo(
    () => ({
      click: isFromGetNotifiedBtn || isFromEmailSyncBtn || isFromDelayedShipmentBtn,
      delayTime: isFromDelayedShipmentBtn ? undefined : delayTime,
      variant: notificationTranslation.identifier,
      popupVariant: identifier,
      cta,
    }),
    [
      cta,
      isFromGetNotifiedBtn,
      isFromEmailSyncBtn,
      isFromDelayedShipmentBtn,
      notificationTranslation.identifier,
      identifier,
      delayTime,
    ],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional mount-only effect
  useEffect(() => {
    gtmViewJoinUs(ga4CommonParams);
  }, []);

  const triggerGtmClickJoinUs = (authenticationMethod: LoginMethod) => {
    gtmClickJoinUs({
      authenticationMethod,
      ...ga4CommonParams,
    });
    logEvent('click_join_us', undefined, {
      authenticationMethod,
      ...(ga4CommonParams.cta !== undefined && { cta: ga4CommonParams.cta }),
      ...(ga4CommonParams.delayTime !== undefined && { delayTime: String(ga4CommonParams.delayTime) }),
      variant: ga4CommonParams.variant ?? '',
      popupVariant: ga4CommonParams.popupVariant ?? '',
      click: String(ga4CommonParams.click),
    });
  };

  const getLoginButtonProps = (loginMethod: LoginMethod) => ({
    newsletterOptIn,
    signUpSource: SignUpSource.ConsumerWeb,
    shouldReloadAfterSuccess: false,
    customHandleClick: () => triggerGtmClickJoinUs(loginMethod),
    customHandleSuccess: (userInfo: IUserInfo, { created }: LoginReplyFlags = {}) =>
      handleSuccess({ userInfo, loginMethod, created }),
  });

  const CheckBoxField = {
    name: 'newsletterOptIn',
    label: subPmcNewsletterText || '',
    type: FieldType.Checkbox,
    isMandatory: false,
    appear: true,
    onClick: setNewsletterOptIn,
  };

  const EmailField = {
    name: 'email',
    label: 'Email Address',
    type: FieldType.Email,
    isMandatory: true,
    customErrorMessage: t(MasterTranslation.EmailFormattingErrorMessage),
  };

  const handleSuccess = ({
    loginMethod,
    userInfo,
    created,
  }: {
    loginMethod: LoginMethod;
    userInfo: IUserInfo;
  } & LoginReplyFlags) => {
    Cookies.set(CookieKey.PmtVerificationStatus, true);
    const isFromJoinUsPopup = isJoinUsPopupCta(cta);
    track.signUpSuccess({
      isNewAccount: created,
      emailSyncOptIn: userInfo.emailSyncOptIn,
      cta,
      delayTime: isFromJoinUsPopup ? delayTime : undefined,
      click: isFromJoinUsPopup ? isFromGetNotifiedBtn : undefined,
      authenticationMethod: loginMethod,
      variant: notificationTranslation.identifier,
      popupVariant: isFromJoinUsPopup ? identifier : undefined,
    });

    // If Email Sync is allowed, we don't need to trigger the Zapier hook when user logins successfully
    // https://www.pivotaltracker.com/story/show/187235872
    if (isEmailSyncAllowed) return;

    triggerHook({
      hookId: zapierHookId,
      data: {
        firstName: userInfo.firstName,
        lastName: userInfo.lastName,
        email: userInfo.email,
        authenticationMethod: userInfo.authenticationMethod,
        profilePictureUrl: userInfo.profilePictureUrl,
        verificationStatus: true,
        newsletterOptIn: newsletterOptIn ? 'Yes' : 'No',
        newsletterOptInSource: NewsletterOptInSource.PMT_WEBSITE,
        linkedInFollowStatus: isFollowingLinkedIn,
        userType: userInfo.userType,
        country: userInfo.country ?? geoInfo.country,
        emailSyncOptIn: userInfo.emailSyncOptIn,
      },
    });
  };

  return {
    handleSuccess,
    getLoginButtonProps,
    triggerGtmClickJoinUs,
    EmailField,
    CheckBoxField,
    cta,
  };
};
